import { useEffect } from 'react';

import { loadPatientKeypairFromIDB } from '../../crypto-module/load-patient-keypair-from-idb.js';

import { usePatientKeypair } from './patient-keypair-context.js';

/**
 * Optionen: injizierbare `now`-Funktion für deterministische Tests; ohne
 * Override greift `Date.now`.
 */
interface UseLoadKeypairIntoContextOptions {
  readonly enabled: boolean;
  readonly now?: () => number;
}

/**
 * Lädt den IDB-Patient-Keypair-Eintrag genau einmal pro `enabled`-Übergang
 * `false → true` in den Keypair-Context.
 *
 * Aufgerufen im `LOADING`-Screen — dort ist der Eintrag entweder:
 *  - durch `useCancelSubmit` frisch persistiert (Submit-Pfad), oder
 *  - durch den vorgelagerten Recovery-Bootstrap als gültig befunden worden.
 *
 * In beiden Fällen ist ein IDB-Read deterministisch und idempotent. Der
 * Context wird nur befüllt, solange noch kein Eintrag drinsteht — so kollidiert
 * der Hook nicht mit dem Recovery-Pfad, der den Context schon initial setzt.
 */
export function useLoadKeypairIntoContext(options: UseLoadKeypairIntoContextOptions): void {
  const { enabled, now } = options;
  const { entry, setEntry } = usePatientKeypair();

  useEffect(() => {
    if (!enabled || entry !== null) {
      return undefined;
    }
    let cancelled = false;
    const resolveNow = now ?? Date.now;
    loadPatientKeypairFromIDB(resolveNow()).then(
      (loaded) => {
        if (cancelled || loaded === null) {
          return;
        }
        setEntry({
          tenantId: loaded.tenantId,
          queueItemId: loaded.queueItemId,
          privateKey: loaded.privateKey,
        });
      },
      () => {
        // IDB-Fehler werden vom Polling-Hook über `enabled === false`
        // abgefangen; ein expliziter Error-Pfad ist hier nicht nötig.
      },
    );
    return () => {
      cancelled = true;
    };
  }, [enabled, entry, setEntry, now]);
}
